// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 3154c472-84e6-4547-9a09-f729ec505468

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — guided-walkthrough content
// ---------------------------------------------------------------------------
// Pure data for Tour.js: two sequences (client user vs. Optimum master), each
// a list of { id, selectors[], title, body, placement }. Every body was
// written from the actual screen it points at — see helpContent.js entries
// for `inventory`, `masterDashboard`, `adminPortal`, `importDialog` and
// `masterCatalog`, which this deliberately matches in register rather than
// duplicates. This module teaches the "?" help-bubble system (one step points
// straight at it) instead of re-explaining what it already covers.
//
// `selectors` is the prioritized list Tour.js walks in order: data-tour
// attribute, then aria-label, then a visible-text match (interactiveOnly:
// false widens the scan to labels/headings, not just buttons/links), then a
// raw CSS selector as a last resort. Several steps below resolve today
// through aria-label or text alone — HelpBubble already renders
// `aria-label="Help: <title>"`, and the AppBar's icons already carry
// `aria-label="Send feedback"` / `"Open help center"` — but every step also
// lists the `data-tour` value it SHOULD get (see the integration report) so
// resolution stops depending on wording that could change under it.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Steps shared by both sequences — reused by reference (not copied) so the
// two lists cannot drift the way CORE_FIELD_KEYS/ALLOWED_FIELDS can.
// ---------------------------------------------------------------------------

const MANAGE_STEP = {
  id: 'manage',
  selectors: [
    { type: 'data-tour', value: 'nav-manage' },
    { type: 'text', value: 'Manage', interactiveOnly: true },
  ],
  title: 'Manage',
  body: "The Manage menu holds the tools that aren't day-to-day browsing: bulk edit and duplicate handling, discovery surveys, infrastructure and document import, contracts and renewals — and Administration, where the fields and lists that shape every fact sheet actually live.",
  placement: 'bottom',
};

const FEEDBACK_STEP = {
  id: 'feedback',
  selectors: [
    { type: 'data-tour', value: 'feedback-icon' },
    { type: 'aria-label', value: 'Send feedback' },
  ],
  title: 'Report a problem or suggest something',
  body: 'This icon sits on every screen for every signed-in user, not just admins — the people who hit a broken screen are rarely the people who administer it. It captures which screen and workspace you were on automatically, and can attach a screenshot.',
  placement: 'bottom',
};

const HELP_CENTER_STEP = {
  id: 'help-center',
  selectors: [
    { type: 'data-tour', value: 'help-center-icon' },
    { type: 'aria-label', value: 'Open help center' },
  ],
  title: 'Help Center',
  body: "The full searchable wiki behind every “?” bubble in the app — every screen, every field, and glossary terms like TIME and 6R explained in one place. It doesn't depend on being on a particular screen, so it's worth bookmarking.",
  placement: 'bottom',
};

const TOUR_TAIL_STEPS = [MANAGE_STEP, FEEDBACK_STEP, HELP_CENTER_STEP];

// ---------------------------------------------------------------------------
// CLIENT sequence
// ---------------------------------------------------------------------------

export const CLIENT_TOUR_STEPS = [
  {
    id: 'welcome',
    selectors: [
      { type: 'data-tour', value: 'brand-wordmark' },
      { type: 'text', value: 'Primordia', interactiveOnly: false },
    ],
    title: 'Welcome to Primordia',
    body: "Every screen sits under this bar, and your workspace's own name and logo stay visible here no matter where you navigate. This is a two-minute tour of the controls you'll reach for most — Next moves forward, and Skip (or Esc) ends it any time.",
    placement: 'bottom',
  },
  {
    id: 'inventory-nav',
    selectors: [
      { type: 'data-tour', value: 'nav-inventory' },
      { type: 'text', value: 'Inventory', interactiveOnly: true },
    ],
    title: 'Inventory',
    body: 'Inventory is the searchable, filterable list of every fact sheet in your workspace — applications, servers, interfaces, whatever your engagement is tracking. Most sessions start here.',
    placement: 'bottom',
  },
  {
    id: 'inventory-search',
    selectors: [
      { type: 'data-tour', value: 'inventory-search' },
      { type: 'css', value: 'input[placeholder="Search fact sheets…"]' },
    ],
    title: 'Search and filters',
    body: "Search by name here, or use the filter rail beside the list — one-click facets for the fields your data actually uses, plus a “More filters” builder that reaches every field on the selected type. A missing value always shows as its own “(not set)” facet instead of being dropped, since during discovery that's usually the bucket worth working next.",
    placement: 'bottom',
  },
  {
    id: 'help-bubble',
    selectors: [
      { type: 'data-tour', value: 'help-bubble-inventory' },
      { type: 'aria-label', value: 'Help: Inventory' },
    ],
    title: 'The “?” bubble',
    body: "Whenever you see a small question-mark icon beside a field or a screen title, click it — it explains exactly what that field means and how it's used elsewhere in Primordia, with a link into the full Help Center for more. It's on every screen; check it before guessing at what a field is for.",
    placement: 'right',
  },
  MANAGE_STEP,
  {
    id: 'import',
    selectors: [
      { type: 'data-tour', value: 'inventory-import' },
      { type: 'text', value: 'Import', interactiveOnly: true },
    ],
    title: 'Import',
    body: 'Import brings in an application inventory from LeanIX, ServiceNow, or a plain spreadsheet. It dry-runs the whole file first — exact create/update/skip counts before anything is written — and re-running the same file updates records instead of duplicating them.',
    placement: 'bottom',
  },
  FEEDBACK_STEP,
  HELP_CENTER_STEP,
];

// ---------------------------------------------------------------------------
// MASTER sequence — an Optimum admin landing in the optimum workspace, which
// deliberately holds no records of its own (see MasterDashboard.js).
// ---------------------------------------------------------------------------

export const MASTER_TOUR_STEPS = [
  {
    id: 'master-kpis',
    selectors: [
      { type: 'data-tour', value: 'master-kpis' },
      { type: 'text', value: 'Active clients', interactiveOnly: false },
    ],
    title: 'Master overview',
    body: 'This is what an Optimum master lands on. The optimum workspace itself holds no records, so this screen gives you every client at a glance instead — live record and application counts, and how many changes landed in the past seven days, all computed server-side per client.',
    placement: 'bottom',
  },
  {
    id: 'master-enter',
    selectors: [
      { type: 'data-tour', value: 'master-client-enter' },
      { type: 'text', value: 'Enter', interactiveOnly: true },
    ],
    title: 'Enter a client',
    body: "Click Enter on any row to switch into that client's actual workspace. Nothing on this overview ever blends two clients into one number — entering one is the only way to see its real records.",
    placement: 'left',
  },
  {
    id: 'workspace-switcher',
    selectors: [
      { type: 'data-tour', value: 'workspace-switcher' },
      { type: 'aria-label', value: 'Workspace switcher' },
    ],
    title: 'Workspace switcher',
    body: 'The same switcher moves you between clients from anywhere in the app, not only from this table — including back to the master optimum workspace to see the cross-client view again.',
    placement: 'bottom',
  },
  {
    id: 'master-catalog',
    selectors: [
      { type: 'data-tour', value: 'master-catalog-link' },
      { type: 'text', value: 'Open the master list', interactiveOnly: true },
    ],
    title: 'Master Application Catalog',
    body: "Optimum's cross-client library of canonical application definitions — vendor, category, typical hosting, default TIME/6R classification, de-identified lessons learned — built once and reused across engagements. Its Reconciliation tab is where a raw application name from any client gets mapped onto one of these entries.",
    placement: 'top',
  },
  {
    id: 'master-admin',
    selectors: [
      { type: 'data-tour', value: 'master-admin-link' },
      { type: 'text', value: 'Users, fields, companies', interactiveOnly: true },
    ],
    title: 'Administration',
    body: "Users, field and list governance, feedback, deleted records, and — master-only — each client's own company profile all live in one screen here, reachable from this tile or from the Manage menu.",
    placement: 'top',
  },
  ...TOUR_TAIL_STEPS,
];

// Picks the sequence for a signed-in session. A master is anyone in the
// OptimumAdmins group, matching every other master check in this app
// (auth.js's isMaster(), CmdbApp's own inline checks) — kept as its own small
// function here rather than importing auth.js, so this file stays pure data
// plus one pure function with no store/DOM dependency of its own.
export function tourStepsForSession(session) {
  const isMaster = !!session && Array.isArray(session.groups) && session.groups.includes('OptimumAdmins');
  return isMaster ? MASTER_TOUR_STEPS : CLIENT_TOUR_STEPS;
}
